import { CellProps, Col } from "./types"

import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { css } from "@emotion/css"
import { useI18n } from "providers/I18n"

type Props<T> = {
  row: Partial<T>
  columns: Col<Partial<T>>[]
  onCancel: VoidFunction
  onCreate: VoidFunction
  onUpdate: (rowId: string, values: Partial<T>) => void
  getError?: (rowId: string, name: keyof T | string) => string | undefined
  colspan?: number
  required?: Col<Partial<T>>[]
  disabledCondition?: () => boolean
}

export function CreateRow<T extends Record<string, any>>(props: Props<T>) {
  const {
    row,
    columns,
    onCancel,
    onCreate,
    onUpdate,
    getError,
    colspan,
    required,
    disabledCondition,
  } = props

  const requiredColumns = !!required?.length
    ? required
        .map((col) => {
          if (col.getOutput) {
            return Object.keys(col.getOutput(row))
          } else {
            return col.name
          }
        })
        .flat()
    : []
  const createColumns = Object.keys(row).filter((k) => !!row[k])
  const requiredOnCreate = !!disabledCondition
    ? !disabledCondition()
    : requiredColumns?.every((elem) => createColumns?.includes(elem as string))

  const cols = [
    ...columns,
    {
      id: "columns.actions",
      cell: AddButtonCell,
      align: "right",
      name: "addButton",
      width: 180,
      colspan: colspan,
    },
  ] as Col<Partial<T>>[]

  return (
    <tr
      role="row"
      style={{
        outline: "1px dashed var(--primary-main)",
      }}
      className={styles.row}
    >
      {cols.map((col) => {
        const {
          id,
          //
          cell,
          newRowCell,
          align = "left",
          width,
          //
          getValue,
          getOutput,
          //
          name,
          colspan,
        } = col
        const Cmp = !!newRowCell ? newRowCell : cell
        return (
          <td
            role="cell"
            key={`new_row_${id}`}
            className={align}
            style={{
              width,
              border: "none",
              paddingTop: 0,
              paddingBottom: 0,
              textAlign: "end",
              verticalAlign: "middle",
            }}
            colSpan={colspan}
          >
            <Cmp
              row={row}
              rowId={"NEW_ROW"}
              name={name}
              getValue={getValue}
              getOutput={getOutput}
              //
              onCancel={onCancel}
              onCreate={onCreate}
              onUpdate={onUpdate}
              //
              getError={getError}
              requiredOnCreate={requiredOnCreate}
            />
          </td>
        )
      })}
    </tr>
  )
}

export function AddButtonCell<T extends Record<string, any>>(
  props: CellProps<T>,
) {
  const { t } = useI18n()
  const { onCancel, onCreate, requiredOnCreate } = props

  return (
    <ButtonGroup>
      <Button variant="outlined" onClick={onCancel} color="primary">
        {t("cancel")}
      </Button>
      <Button
        onClick={onCreate}
        variant="contained"
        color="primary"
        disabled={!requiredOnCreate}
      >
        {t("create")}
      </Button>
    </ButtonGroup>
  )
}

const styles = {
  row: css`
    td {
      font-size: 13px;
      font-weight: normal;
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid var(--border-color-2);
      border-right: 1px solid var(--border-color-2);
      text-align: center;
      :last-child {
        border-right: 0;
      }
      &.right {
        text-align: right;
      }
      &.left {
        text-align: left;
      }
    }
  `,
}
